import React, {CSSProperties, ReactNode, useEffect, useState} from 'react';
import {Tab, Tabs, Box, Typography} from '@mui/material';
import {
  AurDropdownButton,
  AurDropdownButtonProps,
} from '../aur-button/AurDropdownButton';
import AurIcon, {IconNames, IconColors} from '../aur-icon/AurIcon';
import {ReactComponent as ExportIcon} from '../../../../src/assests/svg/export_icon.svg';
import {t} from 'i18next';
import AurButton from '../aur-button/AurButton';

type TabHeader = {
  text: string;
  subText?: string;
};

export type AurTab = {
  header: TabHeader;
  content: ReactNode;
  index: number;
  onClick?: () => void;
  disabled?: boolean;
  type?: string;
  isPreventToChangeTab?: boolean;
  tabProps?: {
    component?: string;
    href?: string;
    target?: string;
    rel?: string;
  };
};

interface TabMenuProps extends Pick<
  AurDropdownButtonProps,
  'renderTooltipWrapper' | 'menuItemStyle'
> {
  tabs: AurTab[];
  width: string;
  indicatorCss?: string;
  headerCss?: string;
  headerClasses?: string;
  contentCss?: string;
  large?: boolean;
  isResponsive?: boolean;
  title?: string;
  defaultTab?: number;
  onTabChange?: (index: number) => void;
  tabMenuHeader?: string;
  tabHeaderMainContainerClassName?: string;
  tabHeaderSubContainerClassName?: string;
  tabHeaderClassName?: string;
  showTabHeaderDropDownButton?: boolean;
  showTabHeaderButton?: boolean;
  onClickTabHeaderButton?: () => void;
  tabHeaderDropdownMenuItem?: {label: string; onClick: () => void}[];
  tabHeaderDropdownMenuItemWidth?:
    'Xlarge' | 'large' | 'mediumSmall' | 'mediumLarge' | 'medium' | 'small';
  tabHeaderDropDownButton?: string;
  dropDownButtonHintText?: string;
  onTabClick?: (tab: AurTab) => void;
  renderText?: (tab: AurTab) => React.ReactNode;
  tabStyle?: CSSProperties;
  forceRenderTabs?: boolean;
}

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  className?: string;
  forceRenderTab?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, forceRenderTab, ...other} = props;

  const renderTabContent = () => {
    const component = <Box sx={{p: 3}}>{children}</Box>;
    if (forceRenderTab) {
      return component;
    }
    if (value === index) {
      return component;
    }
    return <></>;
  };

  return (
    <div
      role="tabpanel"
      id={`simple-tabpanel-${index}`}
      hidden={value !== index}
      aria-hidden={value !== index}
      aria-label={`${t('common.simpleTab')}-${index}`}
      {...other}
    >
      {renderTabContent()}
    </div>
  );
}

const AurTabMenu = (props: TabMenuProps) => {
  const {
    isResponsive,
    defaultTab,
    onTabChange,
    dropDownButtonHintText,
    onTabClick,
    renderText,
    tabStyle = {},
    tabHeaderDropdownMenuItemWidth = 'large',
    ...overflowMenuProps
  } = props;
  const [value, setValue] = React.useState(defaultTab ? defaultTab : 0);
  const [visibleTabCount, setVisibleTabCount] = useState<number>(4); // Default visible tab count
  const [displayTabs, setDisplayTabs] = useState<AurTab[]>(props.tabs);

  // Define breakpoints and tab counts
  const breakpoints = [
    {width: 550, visibleTabs: 1},
    {width: 850, visibleTabs: 2},
    {width: 1050, visibleTabs: 3},
    {width: 1250, visibleTabs: 4},
    {width: 1450, visibleTabs: 5},
    {width: 1650, visibleTabs: 6},
  ];

  const getVisibleTabsCount = () => {
    const screenWidth = window.innerWidth;
    const matchedBreakpoint = breakpoints
      .slice()
      .reverse()
      .find((breakpoint) => screenWidth >= breakpoint.width);

    return matchedBreakpoint ? matchedBreakpoint.visibleTabs : 1; // Default to 1 tab if no match
  };

  useEffect(() => {
    if (defaultTab !== undefined) setValue(defaultTab);
  }, [defaultTab]);

  //commenting this code as this is unused
  // Update visible tab count based on window size
  // const updateVisibleTabCount = () => {
  //   if (isResponsive) {
  //     const width = window.innerWidth;
  //     let tabsToShow = breakpoints[breakpoints.length - 1].visibleTabs; // Default to 6 tabs
  //     for (const breakpoint of breakpoints) {
  //       if (width < breakpoint.width) {
  //         tabsToShow = breakpoint.visibleTabs;
  //         break; // Stop once the first matching breakpoint is found
  //       }
  //     }
  //     setVisibleTabCount(tabsToShow);
  //   } else {
  //     setVisibleTabCount(props.tabs.length); // Show all tabs if not responsive
  //   }
  // };

  const updateTabs = () => {
    const visibleTabsCount = getVisibleTabsCount();
    setVisibleTabCount(visibleTabsCount);

    const visibleTabs = props.tabs.slice(0, visibleTabsCount);
    setDisplayTabs(visibleTabs);
  };

  useEffect(() => {
    updateTabs();
    window.addEventListener('resize', updateTabs);
    return () => window.removeEventListener('resize', updateTabs);
  }, [props.tabs]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const isPreventToChangeTab = displayTabs[newValue].isPreventToChangeTab;
    if (!isPreventToChangeTab) setValue(newValue);

    onTabChange?.(newValue);
  };

  const overflowTabs = props.tabs.slice(visibleTabCount);

  return (
    <>
      <Box
        sx={{
          width: props.width,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
        tabIndex={-1}
      >
        <div className={`${props.tabHeaderMainContainerClassName}`}>
          <div className={`${props.tabHeaderSubContainerClassName}`}>
            {props.tabMenuHeader && (
              <Typography
                className={`typo-text subhead3_size ${props.tabHeaderClassName}`}
                style={
                  props.tabMenuHeader === t('reports.hostingVolume.tab.title')
                    ? {color: '#0C1119'}
                    : {}
                }
              >
                {props.tabMenuHeader}
              </Typography>
            )}
            <Tabs
              value={value}
              onChange={handleChange}
              TabIndicatorProps={{
                className: 'tab-indicator-container',
                children: (
                  <span
                    className={
                      props.indicatorCss
                        ? props.indicatorCss
                        : props.large
                          ? 'tab-indicator-large'
                          : 'tab-indicator-default'
                    }
                  />
                ),
              }}
            >
              {displayTabs.map((tab) => {
                const isSelected = value === tab.index;

                const label = props.large ? (
                  <div aria-hidden="true">
                    <div className="tab-header-large-subtext">
                      {tab.header.subText}
                    </div>
                    <div className="tab-header-large-text">
                      {tab.header.text}
                    </div>
                  </div>
                ) : renderText ? (
                  renderText(tab)
                ) : (
                  <div
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleChange(e, tab.index);
                      }
                    }}
                  >
                    {tab.header.text}
                  </div>
                );
                return (
                  <Tab
                    sx={{textTransform: 'none'}}
                    style={tabStyle}
                    key={tab.index}
                    id={`simple-tab-${tab.index}`}
                    className={
                      props.headerCss
                        ? props.headerCss
                        : props.large
                          ? 'tab-header-large'
                          : `tab-header-default ${props.headerClasses}`
                    }
                    label={label}
                    tabIndex={0}
                    role="tab"
                    aria-selected={isSelected}
                    aria-controls={`simple-tabpanel-${tab.index}`}
                    aria-label={tab.header.text}
                    disabled={tab.disabled}
                    onClick={() => {
                      if (onTabClick) {
                        onTabClick(tab);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleChange(e, tab.index); // activate the tab
                        if (onTabClick) onTabClick(tab);
                      }
                    }}
                    {...tab.tabProps}
                  />
                );
              })}
            </Tabs>
          </div>
          {props.showTabHeaderDropDownButton && (
            <div className="intake-audit-report-drop-down-button-container">
              <AurDropdownButton
                hintText={dropDownButtonHintText}
                dropdownRole="dropdown"
                iconType="Other"
                buttonText={props.tabHeaderDropDownButton}
                classNames="typo-text buttonMedium_size"
                startIcon={<ExportIcon aria-hidden />}
                type="continue"
                size="Medium"
                aria-live="polite"
                iconSize="medium"
                iconClassName="client-profile-details-dropdown-icon"
                menuDirectionLeft={false}
                menuItemHeight="large"
                menuItemWidth="Xlarge"
                menuItemTextSize="large"
                menuClassNames="export-button-drop-down"
                menuItems={props.tabHeaderDropdownMenuItem || []}
              />
            </div>
          )}
          {props?.showTabHeaderButton && (
            <div className="intake-audit-report-drop-down-button-container">
              <AurButton
                onClick={props?.onClickTabHeaderButton}
                startIcon={<ExportIcon aria-hidden />}
                type="continue"
                size="Medium"
                label={t('common.exportXLSX')}
                dataTestId="export-XLSX-btn"
                classNames="typo-text buttonMedium_size"
              />
            </div>
          )}
        </div>
        {isResponsive && overflowTabs.length > 0 && (
          <div className="aur-responsive-tabset-dropdown">
            <AurDropdownButton
              classNames="aur-responsive-tabset-dropdown-icon"
              children={
                <AurIcon
                  icon={IconNames.More}
                  color={IconColors.Custom}
                  customClassName="aur-responsive-tabset-dropdown-icon-color"
                />
              }
              type="secondary"
              aria-live="polite"
              size="Small"
              iconType="Other"
              menuItemHeight="large"
              menuItemWidth={tabHeaderDropdownMenuItemWidth}
              menuItemTextSize="large"
              menuItems={overflowTabs.map((tab) => ({
                label: tab.header.text,
                disabled: tab.disabled,
                onClick: () => {
                  tab?.onClick?.();

                  const isPreventToChangeTab =
                    tab?.isPreventToChangeTab ?? false;

                  !isPreventToChangeTab && setValue(tab.index);

                  onTabClick?.(tab);
                },
              }))}
              {...overflowMenuProps}
            />
          </div>
        )}
      </Box>
      {props.tabs.map((tab) => (
        <TabPanel
          key={tab.index}
          value={value}
          index={tab.index}
          className={props.contentCss}
          forceRenderTab={props.forceRenderTabs}
        >
          {tab.content}
        </TabPanel>
      ))}
    </>
  );
};

export default AurTabMenu;
